import React, { useState } from "react";
import {
    FaBell,
    FaHistory,
    FaCheckCircle,
    FaTrash,
    FaFilter,
    FaSearch,
    FaEye,
    FaCalendarAlt,
    FaSortAmountDown,
    FaSortAmountUp,
    FaBox,
    FaShoppingCart,
    FaStar,
    FaRupeeSign,
    FaCog,
    FaSms,
    FaMobileAlt,
    FaEnvelope,
    FaDownload,
    FaPrint
} from "react-icons/fa";

function NotificationHistory({ isMobile }) {
    // State for notification history
    const [notifications, setNotifications] = useState([
        {
            id: 1,
            type: 'stock',
            title: 'Low Stock Alert',
            message: 'Paneer Tikka is running low (5 items left)',
            timestamp: '2024-03-20 14:30:00',
            read: true,
            channel: 'sms',
            priority: 'high',
            category: 'inventory'
        },
        {
            id: 2,
            type: 'order',
            title: 'High-Value Order',
            message: 'Order #ORD-78945 worth ₹12,500 received',
            timestamp: '2024-03-20 12:15:00',
            read: true,
            channel: 'sms',
            priority: 'high',
            category: 'orders'
        },
        {
            id: 3,
            type: 'payment',
            title: 'Payment Received',
            message: '₹8,750 payment processed for Order #ORD-78945',
            timestamp: '2024-03-20 09:20:00',
            read: false,
            channel: 'email',
            priority: 'medium',
            category: 'payments'
        },
        {
            id: 4,
            type: 'stock',
            title: 'Critical Stock Level',
            message: 'Basmati Rice needs restocking (2 items left)',
            timestamp: '2024-03-19 11:30:00',
            read: true,
            channel: 'push',
            priority: 'critical',
            category: 'inventory'
        },
        {
            id: 5,
            type: 'system',
            title: 'System Maintenance',
            message: 'Scheduled maintenance on March 22, 2:00 AM - 4:00 AM',
            timestamp: '2024-03-19 16:10:00',
            read: false,
            channel: 'push',
            priority: 'low',
            category: 'system'
        },
        {
            id: 6,
            type: 'order',
            title: 'Order Confirmation',
            message: 'Order #ORD-78946 confirmed for ₹2,450',
            timestamp: '2024-03-19 14:45:00',
            read: true,
            channel: 'email',
            priority: 'medium',
            category: 'orders'
        },
        {
            id: 7,
            type: 'stock',
            title: 'Restock Reminder',
            message: 'Butter Chicken Masala running low (8 items left)',
            timestamp: '2024-03-19 10:15:00',
            read: true,
            channel: 'sms',
            priority: 'medium',
            category: 'inventory'
        },
        {
            id: 8,
            type: 'payment',
            title: 'Payment Failed',
            message: 'Payment failed for Order #ORD-78947. Please check.',
            timestamp: '2024-03-18 18:30:00',
            read: true,
            channel: 'sms',
            priority: 'high',
            category: 'payments'
        },
        {
            id: 9,
            type: 'review',
            title: 'New Customer Review',
            message: 'Customer rated your service 4.5 stars',
            timestamp: '2024-03-18 15:20:00',
            read: true,
            channel: 'email',
            priority: 'low',
            category: 'reviews'
        },
        {
            id: 10,
            type: 'system',
            title: 'System Update Available',
            message: 'New version 2.3.1 available for installation',
            timestamp: '2024-03-18 11:45:00',
            read: false,
            channel: 'push',
            priority: 'medium',
            category: 'system'
        }
    ]);

    // State for UI
    const [filterType, setFilterType] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [dateRange, setDateRange] = useState('all');

    // Mark notification as read
    const markAsRead = (id) => {
        setNotifications(prev =>
            prev.map(notification =>
                notification.id === id ? { ...notification, read: true } : notification
            )
        );
    };

    // Mark all as read
    const markAllAsRead = () => {
        setNotifications(prev =>
            prev.map(notification => ({ ...notification, read: true }))
        );
        alert('All notifications marked as read');
    };

    // Delete notification
    const deleteNotification = (id) => {
        setNotifications(prev =>
            prev.filter(notification => notification.id !== id)
        );
    };

    // Clear all notifications
    const clearAllNotifications = () => {
        if (window.confirm('Are you sure you want to clear all notifications?')) {
            setNotifications([]);
        }
    };

    // Export notifications
    const exportNotifications = () => {
        const data = JSON.stringify(filteredNotifications, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'notifications.json';
        a.click();
        URL.revokeObjectURL(url);
    };

    // Print notifications
    const printNotifications = () => {
        window.print();
    };

    // Filter and sort notifications
    const filteredNotifications = notifications
        .filter(notification => {
            const matchesType = filterType === 'all' || notification.type === filterType;
            const matchesSearch =
                notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                notification.message.toLowerCase().includes(searchQuery.toLowerCase());
            
            // Date filtering logic
            let matchesDate = true;
            if (dateRange !== 'all') {
                const notificationDate = new Date(notification.timestamp);
                const today = new Date();
                const daysDiff = Math.floor((today - notificationDate) / (1000 * 60 * 60 * 24));
                
                switch(dateRange) {
                    case 'today':
                        matchesDate = daysDiff === 0;
                        break;
                    case 'week':
                        matchesDate = daysDiff <= 7;
                        break;
                    case 'month':
                        matchesDate = daysDiff <= 30;
                        break;
                }
            }

            return matchesType && matchesSearch && matchesDate;
        })
        .sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

    // Get unread count
    const unreadCount = notifications.filter(n => !n.read).length;

    // Get priority color
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'critical': return '#ef4444';
            case 'high': return '#f97316';
            case 'medium': return '#3b82f6';
            case 'low': return '#10b981';
            default: return '#6b7280';
        }
    };

    // Get type icon
    const getTypeIcon = (type) => {
        switch (type) {
            case 'stock': return <FaBox />;
            case 'order': return <FaShoppingCart />;
            case 'review': return <FaStar />;
            case 'payment': return <FaRupeeSign />;
            case 'system': return <FaCog />;
            default: return <FaBell />;
        }
    };

    // Get channel icon
    const getChannelIcon = (channel) => {
        switch (channel) {
            case 'sms': return <FaSms />;
            case 'push': return <FaMobileAlt />;
            case 'email': return <FaEnvelope />;
            default: return <FaBell />;
        }
    };

    // Notification types for filtering
    const notificationTypes = [
        { id: 'all', label: 'All Notifications', count: notifications.length },
        { id: 'stock', label: 'Stock', count: notifications.filter(n => n.type === 'stock').length },
        { id: 'order', label: 'Orders', count: notifications.filter(n => n.type === 'order').length },
        { id: 'payment', label: 'Payments', count: notifications.filter(n => n.type === 'payment').length },
        { id: 'system', label: 'System', count: notifications.filter(n => n.type === 'system').length },
        { id: 'review', label: 'Reviews', count: notifications.filter(n => n.type === 'review').length }
    ];

    // Date ranges for filtering
    const dateRanges = [
        { id: 'all', label: 'All Time' },
        { id: 'today', label: 'Today' },
        { id: 'week', label: 'Last 7 Days' },
        { id: 'month', label: 'Last 30 Days' }
    ];

    return (
        <div style={{ padding: isMobile ? '20px' : '30px' }}>
            {/* Header Section */}
            <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                marginBottom: '30px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e0e0e0',
                gap: isMobile ? '15px' : '0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: '#f5f3ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6B46C1',
                        fontSize: '22px'
                    }}>
                        <FaHistory />
                    </div>
                    <div>
                        <h2 style={{
                            margin: '0 0 5px 0',
                            fontSize: isMobile ? '20px' : '24px',
                            fontWeight: '600',
                            color: '#1a1a1a'
                        }}>
                            Notification History
                        </h2>
                        <p style={{
                            margin: 0,
                            fontSize: isMobile ? '14px' : '16px',
                            color: '#666'
                        }}>
                            View all your notification logs and activities
                        </p>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                    justifyContent: isMobile ? 'flex-start' : 'flex-end'
                }}>
                    <button
                        onClick={markAllAsRead}
                        style={{
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                    >
                        <FaCheckCircle /> Mark All as Read
                    </button>

                    <button
                        onClick={clearAllNotifications}
                        style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                    >
                        <FaTrash /> Clear All
                    </button>
                </div>
            </div>

            {/* Stats Summary */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: '15px',
                marginBottom: '30px'
            }}>
                <div style={{
                    backgroundColor: '#ffffff',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #e5e7eb',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
                >
                    <div style={{
                        fontSize: '28px',
                        fontWeight: '700',
                        color: '#6B46C1',
                        marginBottom: '5px'
                    }}>
                        {notifications.length}
                    </div>
                    <div style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '600'
                    }}>
                        Total Notifications
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#ffffff',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #e5e7eb',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
                >
                    <div style={{
                        fontSize: '28px',
                        fontWeight: '700',
                        color: '#f59e0b',
                        marginBottom: '5px'
                    }}>
                        {unreadCount}
                    </div>
                    <div style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '600'
                    }}>
                        Unread Notifications
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#ffffff',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #e5e7eb',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
                >
                    <div style={{
                        fontSize: '28px',
                        fontWeight: '700',
                        color: '#3b82f6',
                        marginBottom: '5px'
                    }}>
                        {notifications.filter(n => n.channel === 'sms').length}
                    </div>
                    <div style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '600'
                    }}>
                        SMS Notifications
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#ffffff',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #e5e7eb',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
                >
                    <div style={{
                        fontSize: '28px',
                        fontWeight: '700',
                        color: '#10b981',
                        marginBottom: '5px'
                    }}>
                        {notifications.filter(n => n.type === 'stock').length}
                    </div>
                    <div style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '600'
                    }}>
                        Stock Alerts
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div style={{
                backgroundColor: '#f9fafb',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid #e5e7eb'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '20px',
                    alignItems: isMobile ? 'stretch' : 'center'
                }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <FaFilter style={{ color: '#6b7280' }} />
                            <span style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#374151'
                            }}>
                                Filter by Type:
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap'
                        }}>
                            {notificationTypes.map(type => (
                                <button
                                    key={type.id}
                                    onClick={() => setFilterType(type.id)}
                                    style={{
                                        padding: '6px 12px',
                                        border: 'none',
                                        backgroundColor: filterType === type.id ? '#6B46C1' : '#ffffff',
                                        color: filterType === type.id ? 'white' : '#374151',
                                        borderRadius: '20px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (filterType !== type.id) {
                                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (filterType !== type.id) {
                                            e.currentTarget.style.backgroundColor = '#ffffff';
                                        }
                                    }}
                                >
                                    {type.label}
                                    <span style={{
                                        fontSize: '11px',
                                        backgroundColor: filterType === type.id ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                                        color: filterType === type.id ? 'white' : '#6b7280',
                                        padding: '2px 6px',
                                        borderRadius: '10px'
                                    }}>
                                        {type.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        flexWrap: 'wrap'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <FaCalendarAlt style={{ color: '#6b7280' }} />
                                <span style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    Date Range:
                                </span>
                            </div>
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'wrap'
                            }}>
                                {dateRanges.map(range => (
                                    <button
                                        key={range.id}
                                        onClick={() => setDateRange(range.id)}
                                        style={{
                                            padding: '6px 12px',
                                            border: `1px solid ${dateRange === range.id ? '#6B46C1' : '#d1d5db'}`,
                                            backgroundColor: dateRange === range.id ? '#6B46C1' : 'white',
                                            color: dateRange === range.id ? 'white' : '#374151',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {range.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <button
                                onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                                style={{
                                    padding: '8px 16px',
                                    border: '1px solid #d1d5db',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6B46C1'}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                            >
                                {sortOrder === 'newest' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                                {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
                            </button>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flex: 1
                            }}>
                                <FaSearch style={{ color: '#6b7280' }} />
                                <input
                                    type="text"
                                    placeholder="Search notifications..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        padding: '10px 15px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        width: '100%',
                                        minWidth: '200px',
                                        boxSizing: 'border-box',
                                        transition: 'all 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6B46C1'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Export/Print Actions */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginBottom: '20px'
            }}>
                <button
                    onClick={exportNotifications}
                    style={{
                        padding: '8px 16px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        color: '#374151',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                        e.currentTarget.style.borderColor = '#6B46C1';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                >
                    <FaDownload /> Export
                </button>
                <button
                    onClick={printNotifications}
                    style={{
                        padding: '8px 16px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        color: '#374151',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                        e.currentTarget.style.borderColor = '#6B46C1';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                >
                    <FaPrint /> Print
                </button>
            </div>

            {/* Notifications List */}
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                minHeight: '400px',
                marginBottom: '20px',
                maxHeight: '600px',
                overflowY: 'auto',
                scrollBehavior: 'smooth'
            }}>
                {filteredNotifications.length === 0 ? (
                    <div style={{
                        padding: '60px 20px',
                        textAlign: 'center',
                        color: '#6b7280'
                    }}>
                        <FaBell style={{
                            fontSize: '64px',
                            marginBottom: '20px',
                            opacity: 0.3
                        }} />
                        <h4 style={{
                            margin: '0 0 10px 0',
                            color: '#374151',
                            fontSize: '18px'
                        }}>
                            No notifications found
                        </h4>
                        <p style={{
                            fontSize: '14px',
                            margin: 0
                        }}>
                            Try adjusting your filters or search terms
                        </p>
                    </div>
                ) : (
                    filteredNotifications.map(notification => (
                        <div
                            key={notification.id}
                            style={{
                                padding: '20px',
                                borderBottom: '1px solid #e5e7eb',
                                backgroundColor: notification.read ? '#fff' : '#f8fafc',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '15px',
                                position: 'relative',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = notification.read ? '#fff' : '#f8fafc'}
                        >
                            {/* Unread Indicator */}
                            {!notification.read && (
                                <div style={{
                                    position: 'absolute',
                                    left: '0',
                                    top: '0',
                                    bottom: '0',
                                    width: '4px',
                                    backgroundColor: getPriorityColor(notification.priority)
                                }}></div>
                            )}

                            {/* Notification Icon */}
                            <div style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '10px',
                                backgroundColor: getPriorityColor(notification.priority) + '20',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: getPriorityColor(notification.priority),
                                fontSize: '20px',
                                flexShrink: 0
                            }}>
                                {getTypeIcon(notification.type)}
                            </div>

                            {/* Notification Content */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    marginBottom: '8px',
                                    gap: isMobile ? '10px' : '0'
                                }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginBottom: '5px',
                                            flexWrap: 'wrap'
                                        }}>
                                            <h4 style={{
                                                margin: 0,
                                                fontSize: '16px',
                                                color: '#1a1a1a',
                                                fontWeight: notification.read ? '600' : '700',
                                                wordBreak: 'break-word'
                                            }}>
                                                {notification.title}
                                            </h4>
                                            <span style={{
                                                fontSize: '11px',
                                                color: 'white',
                                                backgroundColor: getPriorityColor(notification.priority),
                                                padding: '3px 10px',
                                                borderRadius: '12px',
                                                textTransform: 'uppercase',
                                                fontWeight: 'bold',
                                                flexShrink: 0
                                            }}>
                                                {notification.priority}
                                            </span>
                                        </div>
                                        <p style={{
                                            margin: '0 0 12px 0',
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            lineHeight: '1.6',
                                            wordBreak: 'break-word'
                                        }}>
                                            {notification.message}
                                        </p>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                        alignItems: isMobile ? 'flex-start' : 'flex-end'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '15px',
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{
                                                fontSize: '12px',
                                                color: '#9ca3af',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                flexShrink: 0
                                            }}>
                                                <FaCalendarAlt /> {notification.timestamp}
                                            </span>

                                            <span style={{
                                                fontSize: '12px',
                                                color: '#6b7280',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                flexShrink: 0
                                            }}>
                                                {getChannelIcon(notification.channel)}
                                                <span style={{ textTransform: 'capitalize' }}>
                                                    {notification.channel}
                                                </span>
                                            </span>
                                        </div>

                                        {/* Action Buttons */}
                                        <div style={{
                                            display: 'flex',
                                            gap: '8px',
                                            flexWrap: 'wrap'
                                        }}>
                                            {!notification.read && (
                                                <button
                                                    onClick={() => markAsRead(notification.id)}
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        color: '#3b82f6',
                                                        border: '1px solid #3b82f6',
                                                        padding: '6px 12px',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        flexShrink: 0,
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#3b82f6';
                                                        e.currentTarget.style.color = 'white';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                        e.currentTarget.style.color = '#3b82f6';
                                                    }}
                                                >
                                                    <FaCheckCircle /> Mark as Read
                                                </button>
                                            )}

                                            <button
                                                onClick={() => deleteNotification(notification.id)}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: '#ef4444',
                                                    border: '1px solid #ef4444',
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    flexShrink: 0,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#ef4444';
                                                    e.currentTarget.style.color = 'white';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = '#ef4444';
                                                }}
                                            >
                                                <FaTrash /> Delete
                                            </button>

                                            <button
                                                onClick={() => alert(`Viewing details for: ${notification.title}`)}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: '#10b981',
                                                    border: '1px solid #10b981',
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    flexShrink: 0,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#10b981';
                                                    e.currentTarget.style.color = 'white';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = '#10b981';
                                                }}
                                            >
                                                <FaEye /> View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination Info */}
            {filteredNotifications.length > 0 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '15px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '14px',
                    color: '#6b7280'
                }}>
                    <span>
                        Showing {filteredNotifications.length} of {notifications.length} notifications
                    </span>
                    <span>
                        {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                    </span>
                </div>
            )}
        </div>
    );
}

export default NotificationHistory;